import React from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import CopyRightInfo from "../../../../core/common/copyRightInfo";
import useFetch from "../../../../hooks/useFetch";
import { API } from "../../../../common/Constants";
import { getAPIHeaders } from "../../../../common/Utils";
import ChatSettingsMenu from "./common/chat-setting-menu";
import { RoleCredit, TokenAllocationSummary } from "../../../../types/dataModel";
import PreLoader from "../../../../core/common/preLoader";
import CustomToast from "../../../../core/common/customToast";
import CommonTabMenu from "../commonTabMenu";

const ChatSettings = () => {
  const routes = all_routes;
  const { isLoading, error, callFetch } = useFetch();
  const [roleUserCount, setRoleUserCount] = React.useState<any[]>([]);
  const [chatTokenLimits, setChatTokenLimits] = React.useState<TokenAllocationSummary>({});
  const [chatTokenRoleLimits, setChatTokenRoleLimits] = React.useState<RoleCredit[]>([]);
  const [toastSettings, setToastSettings] = React.useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO" | "";
  }>({
    show: false,
    message: "",
    heading: "",
    type: "ERROR",
  });
  const [costInINR, setCostInINR] = React.useState<number>(0);

  React.useEffect(() => {
    fetchRoleUserCount();
    fetchTokenRoleLimits();
    fetchFeatureTokenLimits();
  }, []);

  React.useEffect(() => {
    if(estimateTotalMonthlyUsage() > 0) {
      estimateCostInInr();
    }
  }, [roleUserCount, chatTokenRoleLimits]);

  React.useEffect(() => {
      if (error) {
        if (error.response?.data) {
          setToastSettings({
            message: error?.response?.data,
            heading: "Ooops",
            show: true,
            type: "ERROR"
          })
        } else {
          setToastSettings({
            message: 'Something went wrong please try again later.',
            heading: "Ooops",
            show: true,
            type: "ERROR"
          })
        }
      }
  }, [error]);

  const fetchTokenRoleLimits = () => {
    callFetch(API.SETTINGS_AI_ROLE_LIMITS,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((res) => {
      setChatTokenRoleLimits(res.AI_CA_001)
    }).catch((err) => {});
  }

  const fetchFeatureTokenLimits = () => {
    callFetch(API.SETTINGS_AI_FEATURE_ALLOCATION.replace(':featureId', 'AI_CA_001'),
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((res) => {
      setChatTokenLimits(res)
    }).catch((err) => {});
  }

    const fetchRoleUserCount = () => {
      callFetch(API.AI_FEATURE_USER_COUNT.replace(':featureId', 'AI_CA_001'),
        'GET',
        getAPIHeaders(),
        null,
        null
      ).then((res) => {
        setRoleUserCount(res)
      }).catch((err) => { });
    }

  const estimateCostInInr = () =>  {
    callFetch(API.AI_FEATURE_COST_ESTIMATE.replace(':featureId', 'AI_CA_001'),
      'POST',
      getAPIHeaders(),
      {
        tokenCount: estimateTotalMonthlyUsage(),
      },
      null
    ).then((res) => {
      setCostInINR(res);
    }).catch((err) => {});
  }

  const isWithinUsageLimit = () => {
    let totalMonthlyUsage = estimateTotalMonthlyUsage()
    return totalMonthlyUsage <= (chatTokenLimits?.effectiveFeatureMaxLimit || 0);
  }

  const estimateTotalMonthlyUsage = () => {
    let totalMonthlyUsage = 0;
    chatTokenRoleLimits.forEach((role: RoleCredit) => {
      const roleCount = roleUserCount?.find(r => r.roleName === role.roleName)?.count || 0;
      totalMonthlyUsage += (role.monthlyTokenLimit || 0) * roleCount;
    });
    return totalMonthlyUsage ;
  }

  const renderTokenCalculation = () => {
    return (
      <div className="border rounded p-3">
        {(() => {
          const studentRole = chatTokenRoleLimits?.find((r: RoleCredit) => r?.roleName === 'STUDENT');
          const facultyRole = chatTokenRoleLimits?.find((r: RoleCredit) => r?.roleName === 'FACULTY');
          const studentCount = roleUserCount?.find(r => r.roleName === 'STUDENT')?.count || 0;
          const facultyCount = roleUserCount?.find(r => r.roleName === 'FACULTY')?.count || 0;

          const totalMonthlyUsage =
            (studentRole?.monthlyTokenLimit || 0) * studentCount +
            (facultyRole?.monthlyTokenLimit || 0) * facultyCount;

          const isWithinLimit = isWithinUsageLimit();

          return (
            <>
              <h5 className="mb-3">Token Allocation Check</h5>
              <div className="d-flex flex-column">
                <h6 className="mb-2">Total Tokens: <span className="text-primary">{chatTokenLimits?.effectiveFeatureMaxLimit || 0}</span> , Topups: <span className="text-secondary">{chatTokenLimits?.totalTopUps || 0} </span></h6>
                <h6 className="mb-2"> Token Distributions:  <small className="text-muted mb-2">
                  ({studentRole?.monthlyTokenLimit || 0} × {studentCount} Student users) +
                  ({facultyRole?.monthlyTokenLimit || 0} × {facultyCount} Faculty users) = {totalMonthlyUsage} tokens ({costInINR} INR)
                </small> </h6>
                <div className={`alert ${isWithinLimit ? 'alert-success' : 'alert-danger'} mb-0`}>
                  <i className={`me-2 ti ti-${isWithinLimit ? 'check' : 'alert-circle'}`}></i>
                  {isWithinLimit
                    ? `Token distribution is within limits. Using ${totalMonthlyUsage} out of ${chatTokenLimits?.effectiveFeatureMaxLimit || 0} allocated tokens.`
                    : `Token distribution exceeds limits. Required ${totalMonthlyUsage} tokens but only ${chatTokenLimits?.effectiveFeatureMaxLimit || 0} allocated.`
                  }
                </div>
              </div>
            </>
          );
        })()}
      </div>)
  }

  const saveTokenRoleLimits = (e:any) => {
    e?.preventDefault();


    if (chatTokenRoleLimits?.find((role:RoleCredit) => role?.monthlyTokenLimit === null || role?.monthlyTokenLimit === 0 
      || !role?.monthlyTokenLimit) ) {
      setToastSettings({
        message: 'Token monthly limit cannot be empty or zero.',
        heading: "Error",
        show: true,
        type: "ERROR"
      });
      return;
    }
    callFetch(API.SETTINGS_AI_ROLE_LIMIT_UPDATE,
      'PUT',
      getAPIHeaders(),
      null,
      chatTokenRoleLimits.map((r) => {return {
        id: r?.id,
        monthlyTokenLimit: r?.monthlyTokenLimit,
      }})
    ).then((res) => {
      setToastSettings({
        message: 'Chat token limits updated successfully.',
        heading: "Success",
        show: true,
        type: "SUCCESS"
      });
      fetchTokenRoleLimits();
    }).catch((err) => {});
  }

  const onToastClose = () => {
    setToastSettings({
      show: false,
      message: "",
      heading: "",
      type: "",
    });
  };

  const reset = (e:any) =>{
    e?.preventDefault();
    fetchTokenRoleLimits()
  }


  return (
    <div>
      <>
        {/* Page Wrapper */}
        <CustomToast
        show={toastSettings?.show}
        message={toastSettings?.message}
        heading={toastSettings?.heading}
        type={toastSettings?.type || "INFO"}
        onClose={onToastClose} />
        <PreLoader showLoader={isLoading} />
        <div className="page-wrapper">
          <div className="content">
            {/* Breadcrumb */}
            <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
              <div className="my-auto mb-2">
                <h2 className="mb-1">Chat Settings</h2>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={routes.adminDashboard}>
                        <i className="ti ti-smart-home" />
                      </Link>
                    </li>
                    <li className="breadcrumb-item">Administration</li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Settings
                    </li>
                  </ol>
                </nav>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
            {/* /Breadcrumb */}
            <CommonTabMenu activeTab="Chat" />
            <div className="row">
              <div className="col-xl-3 theiaStickySidebar">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex flex-column list-group settings-list">
                     <ChatSettingsMenu activeTab="chatSettings" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-9">
                <div className="card">
                  <div className="card-body">
                    <div className="border-bottom mb-3 pb-3">
                      <h4>Chat Token Monthly Limit</h4>
                    </div>
                    <form>
                      <div className="border-bottom mb-3">
                        <div className="row">
                          <div className="col-md-12">
                            <div className="row align-items-center mb-3">
                              <div className="col-12">
                                {renderTokenCalculation()}
                              </div>
                            </div>
                            <div className="row align-items-center">
                              <div className="col-lg-4">
                                <div className="mb-3">
                                  <h6 className="mb-2 fw-medium">Student Monthly Token Limit</h6>
                                </div>
                              </div>
                              <div className="col-lg-4">
                                <div className="mb-3">
                                  <input
                                    type="number"
                                    min={0}
                                    className="form-control"
                                    placeholder="Enter Token Count"
                                    value={chatTokenRoleLimits?.find((role:RoleCredit) => role?.roleName === 'STUDENT')?.monthlyTokenLimit || ''}
                                    onChange={(e) => {
                                      const updatedLimits:any[] = chatTokenRoleLimits?.map((role: RoleCredit) => {
                                        if (role.roleName === 'STUDENT') {
                                          return { ...role, monthlyTokenLimit: parseInt(e.target.value) };
                                        }
                                        return role;
                                      });
                                      setChatTokenRoleLimits(updatedLimits);
                                    }}
                                  />

                                </div>
                              </div>
                            </div>
                            <div className="row align-items-center">
                              <div className="col-lg-4">
                                <div className="mb-3">
                                  <h6 className="mb-2 fw-medium">Faculty Monthly Token Limit</h6>
                                </div>
                              </div>
                              <div className="col-lg-4">
                                <div className="mb-3">
                                 <input
                                    type="number"
                                    className="form-control"
                                    min={0}
                                    placeholder="Enter Token Count"
                                    value={chatTokenRoleLimits?.find((role:RoleCredit) => role?.roleName === 'FACULTY')?.monthlyTokenLimit || ''}
                                    onChange={(e) => {
                                      const updatedLimits:any[] = chatTokenRoleLimits?.map((role: RoleCredit) => {
                                        if (role.roleName === 'FACULTY') {
                                          return { ...role, monthlyTokenLimit: parseInt(e.target.value) };
                                        }
                                        return role;
                                      });
                                      setChatTokenRoleLimits(updatedLimits);
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center justify-content-end">
                        <button
                          type="button"
                          className="btn btn-outline-light border me-3"
                          onClick={reset}
                        >
                          Cancel
                        </button>
                        <button type="submit" 
                          className="btn btn-primary"
                          disabled={!isWithinUsageLimit()}
                          onClick={saveTokenRoleLimits}>
                          Save
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
            <CopyRightInfo/>
          </div>
        </div>
        {/* /Page Wrapper */}
      </>

    </div>
  );
};

export default ChatSettings;